import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Download, CheckCircle2, Video, Image, Music, FileText, ExternalLink, Loader2, FolderTree, Library, AlertCircle, Pencil, X, Folder } from "lucide-react";
import { KnowledgeTreePanel } from "@/components/KnowledgeTree";
import { UploadModal } from "@/components/UploadResourceModal";

const TYPE_ICON = { video: Video, image: Image, audio: Music, document: FileText };
const TYPE_LABEL = { video: "Video", image: "Imagen", audio: "Audio", document: "Documento" };

export default function BibliotecaRecursos() {
  const [tree, setTree] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [editing, setEditing] = useState(null);   // recurso en edición
  const [saving, setSaving] = useState(false);
  // v5.6.3 · «Mis recursos»: lo que el usuario ya descargó/abrió queda guardado.
  const [showMine, setShowMine] = useState(false);
  const [mine, setMine] = useState([]);

  // v5.7.3 · «Mis recursos» organizados por carpetas: Área → Tema
  const mineFolders = useMemo(() => {
    const m = {};
    mine.forEach((r) => {
      const a = r.area || "General";
      const t = r.topic_title || "Sin tema";
      ((m[a] = m[a] || {})[t] = m[a][t] || []).push(r);
    });
    return Object.keys(m).sort().map((a) => ({ area: a, topics: Object.keys(m[a]).sort().map((t) => ({ topic: t, items: m[a][t] })) }));
  }, [mine]);

  const fetchTree = useCallback(async () => {
    try { setTree((await api.get("/knowledge-tree")).data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar el árbol"); }
  }, []);

  const fetchResources = useCallback(async (nid) => {
    if (!nid) return;
    setLoading(true);
    try { setResources((await api.get("/resource-library?topic_id=" + nid)).data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar recursos"); }
    setLoading(false);
  }, []);

  const fetchMine = useCallback(async () => {
    setLoading(true);
    try { setMine((await api.get("/resource-library/mine/downloaded")).data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al cargar tus recursos"); }
    setLoading(false);
  }, []);
  useEffect(() => { fetchMine(); }, [fetchMine]);
  const markDownloaded = (id) => {
    api.post("/resource-library/" + id + "/downloaded").then(() => {
      setResources((rs) => rs.map((r) => (r.id === id ? { ...r, downloaded: true } : r)));
      api.get("/resource-library/mine/downloaded").then((r) => setMine(r.data)).catch(() => {});
    }).catch(() => {});
  };

  useEffect(() => { fetchTree(); }, [fetchTree]);
  useEffect(() => { if (selectedNodeId) fetchResources(selectedNodeId); }, [selectedNodeId, fetchResources]);

  // Polling: refresca cada 4s si hay recursos en estado "uploading"
  useEffect(() => {
    const hasUploading = resources.some(r => r.status === "uploading");
    if (!hasUploading || !selectedNodeId) return;
    const id = setInterval(() => fetchResources(selectedNodeId), 4000);
    return () => clearInterval(id);
  }, [resources, selectedNodeId, fetchResources]);

  const selectedNode = tree.find(n => n.id === selectedNodeId);

  const handleAddChild = async (parentNode, title) => {
    try {
      await api.post("/knowledge-tree", { area: parentNode.area, parent_id: parentNode.id, title, order: 0 });
      toast.success("Subtema creado");
      fetchTree();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al crear subtema"); }
  };

  // v5.4.0 · Eliminar la jerarquía (el tema y todos sus subtemas; los recursos solo se desvinculan)
  const handleDeleteNode = async (node) => {
    try {
      const { data: c } = await api.get("/knowledge-tree/" + node.id + "/subtree-count");
      const msg = c.is_root
        ? (c.subtopics === 0 ? "Esta área no tiene subtemas." : "¿Vaciar «" + node.title + "»? Se eliminarán " + c.subtopics + " subtema(s). El área se conserva.")
        : "¿Eliminar «" + node.title + "»" + (c.subtopics ? " y sus " + c.subtopics + " subtema(s)" : "") + "?";
      if (c.is_root && c.subtopics === 0) { toast.info(msg); return; }
      const extra = c.resources ? "\n\nLos " + c.resources + " recurso(s) vinculados NO se borran: quedan sin tema asignado." : "";
      if (!window.confirm(msg + extra)) return;
      await api.delete("/knowledge-tree/" + node.id, { params: { cascade: true } });
      toast.success("Jerarquía eliminada");
      setSelectedNodeId(null);
      fetchTree();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo eliminar la jerarquía"); }
  };

  const handleSaveEdit = async () => {
    if (!editing || !editing.title.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    try {
      await api.patch("/resource-library/" + editing.id, { title: editing.title.trim(), description: editing.description || "" });
      toast.success("Recurso actualizado");
      setEditing(null);
      fetchResources(selectedNodeId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se pudo guardar"); }
    setSaving(false);
  };

  const handleDelete = async (resId) => {
    if (!window.confirm("¿Eliminar este recurso?")) return;
    try {
      await api.delete("/resource-library/" + resId);
      toast.success("Recurso eliminado");
      fetchResources(selectedNodeId);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al eliminar"); }
  };

  const renderCard = (res) => {
    const Icon = TYPE_ICON[res.resource_type] || Video;
                  return (
                    <div key={res.id} className="flex items-start gap-3 bg-white rounded-xl border border-[#E1E5F0] p-4" data-testid={"resource-card-" + res.id}>
                      <div className="w-10 h-10 rounded-lg bg-[#A43790]/10 flex items-center justify-center shrink-0"><Icon size={18} className="text-[#A43790]" /></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="text-sm font-bold text-[#1F2547] truncate">{res.title}</h3>
                          <span className="text-[10px] font-semibold text-[#6E7591] bg-[#F0F1F5] px-1.5 py-0.5 rounded">{TYPE_LABEL[res.resource_type]}</span>
                          {res.downloaded && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold text-[#1F8A4C] bg-[#3AE880]/20 px-1.5 py-0.5 rounded" data-testid={"resource-downloaded-" + res.id}><CheckCircle2 size={10} /> Descargado</span>}
                        </div>
                        <p className="text-xs text-[#6E7591] mt-0.5 line-clamp-2">{res.description}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {res.status === "uploading" && <span className="flex items-center gap-1 text-[10px] font-semibold text-[#B8860B]"><Loader2 size={12} className="animate-spin" /> Procesando...</span>}
                        {res.status === "failed" && <span className="flex items-center gap-1 text-[10px] font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded" title={res.error || ""}><AlertCircle size={12} /> Error al subir</span>}
                        {res.status !== "uploading" && res.web_view_link && <a href={res.web_view_link} target="_blank" rel="noopener noreferrer" onClick={() => markDownloaded(res.id)} className="p-1.5 rounded-lg hover:bg-[#F0F1F5] transition-colors" data-testid={"resource-view-" + res.id}><ExternalLink size={14} className="text-[#3B3F5C]" /></a>}
                        <button onClick={() => setEditing({ id: res.id, title: res.title || "", description: res.description || "" })} className="p-1.5 rounded-lg hover:bg-[#F0F1F5] transition-colors" title="Editar" aria-label="Editar recurso" data-testid={"resource-edit-" + res.id}><Pencil size={14} className="text-[#3B3F5C]" /></button>
                        <button onClick={() => handleDelete(res.id)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" data-testid={"resource-delete-" + res.id}><Trash2 size={14} className="text-red-500" /></button>
                      </div>
                    </div>
                  );
  };

  return (
    <div className="flex h-[calc(100vh-64px)] bg-[#F7F8FC]" data-testid="biblioteca-recursos-page">
      <div className="w-80 shrink-0 border-r border-[#E1E5F0] bg-white overflow-y-auto">
        <div className="px-4 py-3 border-b border-[#E1E5F0] flex items-center gap-2">
          <FolderTree size={16} className="text-[#A43790]" />
          <h2 className="text-sm font-bold text-[#1F2547]">Árbol de Conocimiento</h2>
        </div>
        <div className="py-1">
          <KnowledgeTreePanel tree={tree} selectedNodeId={selectedNodeId} onSelect={setSelectedNodeId} onAddChild={handleAddChild} onDelete={handleDeleteNode} />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="flex items-center gap-2 mb-4" data-testid="library-mine-bar">
          <button onClick={() => setShowMine((v) => !v)} data-testid="toggle-my-resources"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border-2 transition-colors ${showMine ? "bg-[#A43790] border-[#A43790] text-white" : "border-[#A43790] text-[#A43790] hover:bg-[#FFE9F0]"}`}>
            <Download size={14} /> Mis recursos descargados ({mine.length})
          </button>
          {showMine && <span className="text-[11px] text-[#6E7591]">Se guardan en tu biblioteca aunque cierres sesión.</span>}
        </div>
        {!selectedNodeId && !showMine ? (
          <div className="flex flex-col items-center justify-center h-full text-[#6E7591]">
            <Library size={48} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">Selecciona un tema del árbol para ver sus recursos</p>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-bold text-[#1F2547]">{showMine ? "Mis recursos descargados" : (selectedNode ? selectedNode.title : "Recursos")}</h2>
              {!showMine && <button onClick={() => setShowUpload(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#A43790] text-white text-xs font-bold hover:bg-[#7B2A6C] transition-colors" data-testid="btn-upload-resource">
                <Upload size={14} /> Subir recurso
              </button>}
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-12"><Loader2 size={24} className="animate-spin text-[#A43790]" /></div>
            ) : (showMine ? mine : resources).length === 0 ? (
              <p className="text-sm text-[#6E7591] text-center py-12">{showMine ? "Aún no has descargado ningún recurso. Al abrir uno con el ícono ↗ se guarda aquí." : "No hay recursos en este tema todavía."}</p>
            ) : (
              <div className="grid gap-3">
                {showMine && mineFolders.map((f) => (
                  <details key={f.area} open className="rounded-xl border border-[#E1E5F0] bg-[#FAF8FC]" data-testid={"mine-folder-" + f.area}>
                    <summary className="cursor-pointer select-none px-4 py-2.5 text-sm font-extrabold text-[#A43790] inline-flex items-center gap-2 w-full">
                      <Folder size={16} /> {f.area} <span className="text-[11px] font-semibold text-[#6E7591]">({f.topics.reduce((n, t) => n + t.items.length, 0)})</span>
                    </summary>
                    <div className="px-3 pb-3 space-y-2">
                      {f.topics.map((t) => (
                        <details key={t.topic} open className="rounded-lg bg-white border border-[#EEE0F0]">
                          <summary className="cursor-pointer select-none px-3 py-2 text-[13px] font-bold text-[#1F2547] inline-flex items-center gap-2 w-full">
                            <Folder size={14} className="text-[#F5B82E]" /> {t.topic} <span className="text-[11px] font-semibold text-[#6E7591]">({t.items.length})</span>
                          </summary>
                          <div className="grid gap-2 p-2">{t.items.map((res) => renderCard(res))}</div>
                        </details>
                      ))}
                    </div>
                  </details>
                ))}
                {!showMine && resources.map(res => renderCard(res))}
              </div>
            )}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[60] bg-[#1F2547]/60 grid place-items-center p-4" onClick={() => setEditing(null)} data-testid="resource-edit-modal">
          <div className="w-full max-w-md bg-white rounded-2xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold text-[#1F2547]">Editar recurso</h3>
              <button onClick={() => setEditing(null)} aria-label="Cerrar"><X size={18} className="text-[#6E7591]" /></button>
            </div>
            <label className="block text-xs font-bold text-[#A43790]">Título
              <input value={editing.title} maxLength={200} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="resource-edit-title"
                className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm font-normal text-[#1F2547]" />
            </label>
            <label className="block text-xs font-bold text-[#A43790]">Descripción
              <textarea rows={3} value={editing.description} maxLength={1000} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="resource-edit-desc"
                className="mt-1 w-full px-3 py-2 rounded-lg border-2 border-[#E1E5F0] text-sm font-normal text-[#1F2547]" />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-3 py-1.5 rounded-lg text-xs font-bold text-[#6E7591] hover:bg-[#F0F1F5]">Cancelar</button>
              <button onClick={handleSaveEdit} disabled={saving} data-testid="resource-edit-save" className="px-3 py-1.5 rounded-lg bg-[#A43790] text-white text-xs font-bold disabled:opacity-60">{saving ? "Guardando…" : "Guardar"}</button>
            </div>
          </div>
        </div>
      )}

      {showUpload && <UploadModal selectedNodeId={selectedNodeId} onClose={() => setShowUpload(false)} onSuccess={() => { setShowUpload(false); fetchResources(selectedNodeId); }} />}
    </div>
  );
}
